'use client';

import { FC, useState, useCallback } from 'react';
import { EventType } from '@/services/isomorphic/UserInteractionService';
import { Link } from '@/react/components/core-ui/Link';
import { Icon, IconTypes, IconSizeProp } from '@/react/components/core-ui/Icon';
import { useDrawerActions } from '@/react/components/core-ui/Drawer/hooks';
import { classes } from '@/next-utils/css-utils/scss-utils';
import type { INavigation } from '@/services/models/Navigation/INavigation';
import type { INavigationLinkProps } from '../NavigationLink/INavigationLinkProps';
import { formatLinkName } from '../../../utils';

import S from './styles.base.module.scss';

interface IAccordionSectionProps {
  /** The category to render. */
  category: INavigation;
  /** The parent category name. */
  parentCategoryName: string;
  /** The expanded ID. */
  expandedId: string | null;
  /**
   * Callback invoked when an accordion section is toggled. If the section
   * identified by `id` is currently expanded it will be collapsed, and
   * vice versa.
   * @param id - The unique identifier of the accordion section to toggle.
   */
  onToggle: (id: string) => void;
  /** The function to handle the link click. */
  onLinkClick: () => void;
}

/**
 * A single accordion section for secondary navigation categories.
 * Renders a toggle header with +/- icon and expandable tertiary links.
 */
const AccordionSection: FC<IAccordionSectionProps> = ({
  category,
  parentCategoryName,
  expandedId,
  onToggle,
  onLinkClick
}) => {
  const isExpanded = expandedId === category.id;
  const hasTertiaryLinks =
    category.categories && category.categories.length > 0;

  if (!hasTertiaryLinks) {
    return (
      <Link
        className={S.accordionHeader}
        href={category.href ?? ''}
        onClick={onLinkClick}
        interactionDetails={{
          action: EventType.NavigationLink,
          link: {
            linkName: formatLinkName(parentCategoryName, category.name),
            linkCategory: 'top-navigation',
            linkSection: parentCategoryName.toLowerCase()
          }
        }}
      >
        {category.name}
      </Link>
    );
  }

  return (
    <div>
      <button
        type="button"
        className={S.accordionHeader}
        onClick={() => onToggle(category.id)}
        aria-expanded={isExpanded}
        aria-label={category.name}
      >
        <span>{category.name}</span>
        <span className={S.accordionIcon}>
          <Icon
            icon={isExpanded ? IconTypes.Minus : IconTypes.Plus}
            size={IconSizeProp.Size1X}
          />
        </span>
      </button>

      <div
        className={classes(S.accordionContent, isExpanded && S.accordionOpen)}
      >
        <div className={S.accordionLinks}>
          {category.categories?.map((subCategory) => (
            <Link
              key={subCategory.id}
              className={classes(
                S.accordionLink,
                subCategory.name?.toLowerCase() === 'sale' && S.saleLink
              )}
              href={subCategory.href ?? ''}
              onClick={onLinkClick}
              interactionDetails={{
                action: EventType.NavigationLink,
                link: {
                  linkName: formatLinkName(
                    parentCategoryName,
                    subCategory.name
                  ),
                  linkCategory: 'top-navigation',
                  linkSection: parentCategoryName.toLowerCase()
                }
              }}
            >
              {subCategory.name}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Accordion-style mobile navigation link component.
 *
 * Instead of opening nested drawers for secondary and tertiary categories,
 * it renders secondary categories as expandable accordion items with +/- toggle icons.
 * Tertiary links slide down in-place beneath each section.
 */
export const AccordionNavigationLink: FC<INavigationLinkProps> = ({ item }) => {
  const { closeAllDrawers } = useDrawerActions();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleToggle = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  return (
    <div className={S.columnFullWidth}>
      {item.categories?.map((category) => (
        <AccordionSection
          key={category.id}
          category={category}
          parentCategoryName={item.name}
          expandedId={expandedId}
          onToggle={handleToggle}
          onLinkClick={closeAllDrawers}
        />
      ))}
    </div>
  );
};
